import React, { useState, useEffect, useCallback } from 'react'
import Grid from '@mui/material/Grid'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Tab,
    Tabs,
} from '@mui/material'

import { LoadingDefault } from '@/shared/ui/loading'
import DisplayCustomViewForm from '../../../../Components/Form/DisplayCustomViewForm'
import ConfirmDeleteDialog from '../../../../Components/Form/ConfirmDeleteDialog'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSave } from '@fortawesome/pro-duotone-svg-icons/faSave'
import { faTrashAlt } from '@fortawesome/pro-duotone-svg-icons/faTrashAlt'
import { BlockFormProps, BlockTypes, ItemState } from '../types'

import GeneralTab from '../Components/Tabs/GeneralTab'
import TranslationsTab from '../Components/Tabs/TranslationsTab'
import LayoutTab from '../Components/Tabs/LayoutTab'
import { getBlockSettingsComponent } from '../Components/Tabs/BlockSettingsRegistry'

import PermissionsTab from '../Components/Tabs/PermissionsTab'
import SortTab from '../Components/Tabs/SortTab'
import InfoTab from '../Components/Tabs/InfoTab'

import {
    useRoles,
    useDefDossiers,
    useCustomViews,
    useQueries,
    useBlockFormState,
} from '../hooks'

const BlockForm: React.FC<BlockFormProps> = ({
    kedo,
    item: initialItem,
    onClose,
    onSuccess,
}) => {
    const [tabValue, setTabValue] = useState(0)

    const {
        state,
        handleChangeItem,
        handleChangeSettings,
        handleChangeState,
        handleTemplateOptionsChange,
    } = useBlockFormState(initialItem)
    const { availableRoles, fetchRoles } = useRoles(kedo)
    const { defDossiers, fetchDefDossiers } = useDefDossiers(kedo)
    const { views, fetchCustomViews } = useCustomViews(
        kedo,
        state.item.defDossier
    )
    const { dossierQueries, fetchQueries } = useQueries(
        kedo,
        state.item.defDossier
    )

    useEffect(() => {
        handleChangeState('availableRoles', availableRoles)
    }, [availableRoles, handleChangeState])

    useEffect(() => {
        handleChangeState('defDossiers', defDossiers)
    }, [defDossiers, handleChangeState])

    useEffect(() => {
        handleChangeState('views', views)
    }, [views, handleChangeState])

    useEffect(() => {
        handleChangeState('dossierQueries', dossierQueries)
    }, [dossierQueries, handleChangeState])

    const isEntityRequired = Object.values(BlockTypes).includes(state.item.type)

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue)
    }

    useEffect(() => {
        if (availableRoles.length > 0) {
            const environment = kedo.env().getEnvironment()
            const currentRoles = environment.roles
            if (!kedo.isAdminOrEnvironmentAdmin()) {
                const filteredRoles = availableRoles
                    .filter((role: any) =>
                        currentRoles.some(
                            (envRole: any) => envRole.id === role.id
                        )
                    )
                    .map((rol: any) => rol.id)
                handleChangeItem('roles', filteredRoles)
            }
        }
    }, [availableRoles, kedo, handleChangeItem])

    const handleSuccess = useCallback(() => {
        handleChangeState('submitting', false)
        handleChangeState('errors', false)
        onSuccess(true)
    }, [onSuccess, handleChangeState])

    const hasFormErrors = useCallback((response: any) => {
        return (
            response.response &&
            response.response.status === 400 &&
            response.response.data &&
            response.response.data.errors
        )
    }, [])

    const handleErrors = useCallback(
        (response: any) => {
            if (!hasFormErrors(response)) {
                handleChangeState('errors', {
                    generic: kedo.t('Something went wrong'),
                })
            } else {
                handleChangeState('errors', response.response.data.errors)
            }
            handleChangeState('submitting', false)
        },
        [hasFormErrors, kedo, handleChangeState]
    )

    const handleNormalChangeForSettings = useCallback(
        (
            event: React.ChangeEvent<
                HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
            >
        ) => {
            const { name, value, type } = event.target
            const val = type === 'number' ? Number(value) : value
            handleChangeSettings(name, val)
        },
        [handleChangeSettings]
    )

    const handleChange = useCallback(
        (
            event: React.ChangeEvent<
                HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
            >
        ) => {
            const { name, value } = event.target
            const target = event.target
            const val =
                'type' in target &&
                (target as HTMLInputElement).type === 'checkbox'
                    ? (target as HTMLInputElement).checked
                    : value
            handleChangeItem(name as keyof ItemState, val)
        },
        [handleChangeItem]
    )

    const onChangeTranslation = useCallback(
        (locale: string, title: string) => {
            const myPageBlock = { ...state.item }
            const translations = myPageBlock.translations.filter(
                (item) => item.culture !== locale
            )
            if (title) {
                translations.push({ culture: locale, title: title })
            }
            myPageBlock.translations = translations
            handleChangeItem('translations', translations)
        },
        [state.item, handleChangeItem]
    )

    const onChangeRole = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const roleId = parseInt(event.target.value)
            const isChecked = event.target.checked

            const item = { ...state.item }
            const roles = item.roles.filter((id) => id !== roleId)

            if (isChecked) {
                roles.push(roleId)
            }
            item.roles = roles
            handleChangeItem('roles', roles)
        },
        [state.item, handleChangeItem]
    )

    const submit = useCallback(() => {
        if (
            !state.item.type ||
            (![
                'online_users',
                'funnel',
                'active_users',
                'kedo_charts',
                'new_dossier_statistics',
                'block_container',
            ].includes(state.item.type) &&
                !state.item.defDossier)
        ) {
            const errors: Record<string, string> = {}
            if (!state.item.type || state.item.type.length <= 0) {
                errors.type = kedo.t('This is a required field')
            }
            if (!state.item.defDossier) {
                errors.defDossier = kedo.t('This is a required field')
            }
            handleChangeState('errors', errors)
            return
        }

        handleChangeState('submitting', true)
        handleChangeState('errors', false)

        const baseUrl = kedo.api().getMyPageBlockEndpoint()
        const settings: Record<string, any> = { ...state.item.settings }

        if (state.item.type === 'block_container') {
            settings.layout =
                !state.item.settings.layout ||
                state.item.settings.layout === 'table'
                    ? 'horizontal'
                    : state.item.settings.layout
        }
        /* if (state.item.type === 'dashboard') {
        } else if (state.item.type === 'list_option_percentage_conditions') {
        } else if (state.item.type === 'count_list_options') {
        } else if (state.item.type === 'sum_fields') {
        } else if (state.item.type === 'kedo_charts') {
        } else if (state.item.type === 'block_container') {
            settings.layout = (!state.item.settings.layout || state.item.settings.layout === 'table') ? 'horizontal' : state.item.settings.layout;
        } else if (state.item.type === 'date_statistics') {
        } else if (state.item.type === 'count_link_options') {
        } else {
        } */

        const payload: Record<string, any> = {
            translations: state.item.translations,
            settings: settings,
            parent: state.item.parent_id,
            type: state.item.type,
            col: state.item.col,
            defDossier: state.item.defDossier,
            dashboard: state.item.dashboard,
            rank: state.item.rank,
            dossierQuery: state.item.dossierQuery,
            roles: state.item.roles,
        }

        if (state.item.id) {
            kedo.api()
                .patch(baseUrl + '/' + state.item.id, payload)
                .then(() => handleSuccess())
                .catch((errResponse: any) => handleErrors(errResponse))
        } else {
            payload.environment = kedo.env().getEnvironment().id
            payload.uuid = kedo.utils().uuidv4()

            kedo.api()
                .post(baseUrl, payload)
                .then(() => handleSuccess())
                .catch((errResponse: any) => handleErrors(errResponse))
        }
    }, [state, kedo, handleSuccess, handleErrors, handleChangeState])

    useEffect(() => {
        fetchDefDossiers()
        fetchRoles()
        if (initialItem) {
            fetchCustomViews()
            fetchQueries()
        }
    }, [
        fetchDefDossiers,
        fetchRoles,
        fetchCustomViews,
        fetchQueries,
        initialItem,
    ])

    const renderSettingsByType = useCallback(
        (blockType: string) => {
            const SettingsComponent = getBlockSettingsComponent(blockType)
            return (
                <SettingsComponent
                    kedo={kedo}
                    state={state}
                    handleChangeSettings={handleChangeSettings}
                    handleChangeState={handleChangeState}
                    handleTemplateOptionsChange={handleTemplateOptionsChange}
                    handleNormalChangeForSettings={
                        handleNormalChangeForSettings
                    }
                    handleChangeItem={handleChangeItem}
                    fetchCustomViews={fetchCustomViews}
                />
            )
        },
        [
            kedo,
            state,
            handleChangeSettings,
            handleChangeState,
            handleTemplateOptionsChange,
            handleNormalChangeForSettings,
            handleChangeItem,
            fetchCustomViews,
        ]
    )

    const showSortTab = state.item.type === 'dashboard'
    const sortTabIndex = 5
    const infoTabIndex = showSortTab ? 6 : 5

    useEffect(() => {
        if (tabValue > infoTabIndex) {
            setTabValue(infoTabIndex)
        }
    }, [infoTabIndex, tabValue])

    return (
        <Dialog
            open={true}
            onClose={onClose}
            maxWidth="xl"
            fullWidth
            aria-labelledby="block-form-title"
        >
            {state.delete && state.item.id && (
                <ConfirmDeleteDialog
                    errorLabel={kedo.t('Something went wrong')}
                    kedo={kedo}
                    url={`${kedo.api().getMyPageBlockEndpoint()}/${
                        state.item.id
                    }`}
                    onClose={() => handleChangeState('delete', false)}
                    onSuccess={() => {
                        onClose()
                        onSuccess(true)
                    }}
                />
            )}

            {state.showAddView && (
                <DisplayCustomViewForm
                    kedo={kedo}
                    onClose={() => handleChangeState('showAddView', false)}
                    onSuccess={() => {
                        fetchCustomViews()
                        handleChangeState('showAddView', false)
                    }}
                />
            )}

            <DialogTitle id="block-form-title">
                {kedo.t(state.item.id ? 'Edit' : 'Add')}{' '}
                {kedo.t('Block').toLowerCase()}
            </DialogTitle>

            <DialogContent dividers>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 3 }}>
                        <Tabs
                            orientation="vertical"
                            variant="scrollable"
                            value={tabValue}
                            onChange={handleTabChange}
                            aria-label="Block form tabs"
                            sx={{ borderRight: 1, borderColor: 'divider' }}
                        >
                            <Tab label={kedo.t('General')} />
                            <Tab label={kedo.t('Translations')} />
                            <Tab label={kedo.t('Layout')} />
                            <Tab label={kedo.t('Block type specific')} />
                            <Tab label={kedo.t('Permissions')} />
                            {showSortTab && <Tab label={kedo.t('Sort')} />}
                            <Tab label={kedo.t('info')} />
                        </Tabs>
                    </Grid>

                    <Grid size={{ xs: 12, md: 9, lg: 8 }}>
                        <Box hidden={tabValue !== 0} sx={{ p: 1 }}>
                            <GeneralTab
                                kedo={kedo}
                                state={state}
                                handleChange={handleChange}
                                handleChangeItem={handleChangeItem}
                                fetchCustomViews={fetchCustomViews}
                                fetchQueries={fetchQueries}
                                isEntityRequired={isEntityRequired}
                            />
                        </Box>

                        <Box hidden={tabValue !== 1} sx={{ p: 1 }}>
                            <TranslationsTab
                                kedo={kedo}
                                state={state}
                                onChangeTranslation={onChangeTranslation}
                            />
                        </Box>

                        <Box hidden={tabValue !== 2} sx={{ p: 1 }}>
                            <LayoutTab
                                kedo={kedo}
                                state={state}
                                handleChange={handleChange}
                            />
                        </Box>

                        <Box hidden={tabValue !== 3} sx={{ p: 1 }}>
                            {renderSettingsByType(state.item.type)}
                        </Box>

                        <Box hidden={tabValue !== 4} sx={{ p: 1 }}>
                            <PermissionsTab
                                kedo={kedo}
                                state={state}
                                onChangeRole={onChangeRole}
                            />
                        </Box>

                        {showSortTab && (
                            <Box
                                hidden={tabValue !== sortTabIndex}
                                sx={{ p: 1 }}
                            >
                                <SortTab
                                    kedo={kedo}
                                    state={state}
                                    handleChangeSettings={handleChangeSettings}
                                />
                            </Box>
                        )}

                        <Box hidden={tabValue !== infoTabIndex} sx={{ p: 1 }}>
                            <InfoTab
                                kedo={kedo}
                                item={initialItem || state.item}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions sx={{ p: 3, justifyContent: 'space-between' }}>
                <Box>
                    {state.item.id && (
                        <Button
                            onClick={() => handleChangeState('delete', true)}
                            variant="outlined"
                            color="error"
                            title={kedo.t('Delete')}
                            size="small"
                            disabled={state.submitting}
                            startIcon={
                                <FontAwesomeIcon icon={faTrashAlt} color="" />
                            }
                        >
                            {kedo.t('Delete')}
                        </Button>
                    )}
                </Box>
                <Box>
                    <Button
                        variant="outlined"
                        title={kedo.t('Cancel')}
                        onClick={onClose}
                        color="secondary"
                        sx={{ mr: 1 }}
                        size="small"
                        disabled={state.submitting}
                    >
                        {kedo.t('Cancel')}
                    </Button>
                    <Button
                        onClick={submit}
                        title={kedo.t('Save')}
                        color="primary"
                        disabled={state.submitting}
                        loading={state.submitting}
                        startIcon={<FontAwesomeIcon icon={faSave} />}
                    >
                        {kedo.t('Save')}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    )
}

export default BlockForm
